// Calculs du tableau de bord comptable (brique C). Fonctions PURES, sans dépendance, 100 %
// testables. Aucun taux n'est codé en dur ici : ils proviennent des ParametresFiscaux de la
// cliente (pré-remplis aux valeurs 2026 mais éditables). Ce sont des ESTIMATIONS d'aide à la
// gestion, jamais un conseil fiscal.

import type { ParametresFiscaux } from '../schemas/reglages'

// Base de calcul du chiffre d'affaires.
// - 'payees'  : seules les factures encaissées (statut 'payee') — base demandée par l'URSSAF.
// - 'emises'  : toutes les factures émises (émises + payées), vision « facturé ».
// Les brouillons et les factures annulées ne comptent jamais.
export type BaseCA = 'payees' | 'emises'

export interface LigneCA {
  statut: string
  dateFacture: string // 'AAAA-MM-JJ'
  // Date d'encaissement (27/10/2026), saisie au passage à « payée ». Absente ou null sur les
  // factures payées auparavant : on retombe alors sur la date de facture.
  dateEncaissement?: string | null
  totalHt: number
}

export interface ResultatTableauBord {
  ca: number
  nbFactures: number
  cotisationsSociales: number // après réduction ACRE éventuelle
  cfp: number
  impotVersementLiberatoire: number | null // si versement libératoire actif, sinon null
  baseImposable: number | null // si PAS de versement libératoire (= CA après abattement)
  totalPrelevements: number // cotisations + CFP + (versement libératoire si actif)
  resteApresPrelevements: number // CA - totalPrelevements (avant IR au barème le cas échéant)
}

const arrondi2 = (n: number): number => Math.round((n + Number.EPSILON) * 100) / 100

/**
 * Garde les factures de la base choisie dont la date tombe dans [debut, fin] (bornes incluses).
 * Base 'payees' : date d'ENCAISSEMENT (celle que retient l'URSSAF), à défaut date de facture.
 * Base 'emises' : date de facture.
 */
export function filtrerFactures(
  lignes: LigneCA[],
  base: BaseCA,
  debut: string,
  fin: string,
): LigneCA[] {
  return lignes.filter((l) => {
    const statutOk =
      base === 'payees' ? l.statut === 'payee' : l.statut === 'emise' || l.statut === 'payee'
    const date = base === 'payees' ? (l.dateEncaissement ?? l.dateFacture) : l.dateFacture
    return statutOk && date >= debut && date <= fin
  })
}

/** Agrège un ensemble de factures déjà filtrées + applique les paramètres fiscaux. */
export function calculerTableauBord(
  lignes: LigneCA[],
  p: ParametresFiscaux,
): ResultatTableauBord {
  const ca = arrondi2(lignes.reduce((s, l) => s + l.totalHt, 0))
  const reductionAcre = p.acreActive ? 1 - p.tauxAcre / 100 : 1
  const cotisationsSociales = arrondi2(ca * (p.tauxCotisation / 100) * reductionAcre)
  const cfp = arrondi2(ca * (p.tauxCfp / 100))
  const impotVersementLiberatoire = p.versementLiberatoire
    ? arrondi2(ca * (p.tauxVersementLiberatoire / 100))
    : null
  const baseImposable = p.versementLiberatoire
    ? null
    : arrondi2(ca * (1 - p.tauxAbattement / 100))
  const totalPrelevements = arrondi2(
    cotisationsSociales + cfp + (impotVersementLiberatoire ?? 0),
  )
  const resteApresPrelevements = arrondi2(ca - totalPrelevements)
  return {
    ca,
    nbFactures: lignes.length,
    cotisationsSociales,
    cfp,
    impotVersementLiberatoire,
    baseImposable,
    totalPrelevements,
    resteApresPrelevements,
  }
}
